import { useCallback, useEffect, useRef, useState } from 'react';
import { getComparisonVersions, importComparisonVersion } from '../../../Services/versionComparison.service';
import { getVersionStatus } from '../../../Services/documents.service';
import { versionComparisonError } from '../../../utils/versionComparison';

export default function useVersionComparison({ documentId, viewedVersionId, enabled }) {
    const [mode, setMode] = useState('document_version');
    const [rows, setRows] = useState([]);
    const [referenceId, setReferenceId] = useState('');
    const examinedId = viewedVersionId || '';
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [tracking, setTracking] = useState({});
    const scope = `${documentId || ''}:${viewedVersionId || ''}`;
    const scopeRef = useRef(scope);
    scopeRef.current = scope;
    const requestRef = useRef(0);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

    useEffect(() => {
        setMode('document_version');
        setRows([]);
        setReferenceId('');
        setTracking({});
        setError('');
        setNotice('');
        setImporting(false);
        setLoading(false);
    }, [scope]);

    const refresh = useCallback(async () => {
        if (!documentId || !viewedVersionId) {
            setError('Le document principal n’est pas encore identifié. Rechargez l’analyse avant de comparer ses versions.');
            return;
        }
        const request = ++requestRef.current;
        setLoading(true);
        try {
            const next = await getComparisonVersions(documentId, viewedVersionId);
            if (!mounted.current || scopeRef.current !== scope || request !== requestRef.current) return;
            setRows(next);
            setError('');
            setTracking(previous => {
                const updated = { ...previous };
                next.forEach(row => {
                    if (row.extraction_status === 'processing' && !updated[row.document_version_id]) {
                        updated[row.document_version_id] = { status: 'processing' };
                    }
                });
                return updated;
            });
        } catch (failure) {
            if (mounted.current && scopeRef.current === scope && request === requestRef.current) {
                setError(versionComparisonError(failure));
                setRows([]);
            }
        } finally {
            if (mounted.current && scopeRef.current === scope && request === requestRef.current) setLoading(false);
        }
    }, [documentId, viewedVersionId, scope]);

    useEffect(() => {
        if (enabled && mode === 'document_version') refresh();
    }, [enabled, mode, refresh]);

    const pollingIds = Object.keys(tracking).filter(id => tracking[id].status === 'processing').join(',');
    useEffect(() => {
        if (!pollingIds) return;
        let cancelled = false;
        const timers = [];
        const poll = async id => {
            try {
                const { data } = await getVersionStatus(id);
                if (cancelled) return;
                const status = data.extraction_status;
                setTracking(previous => ({ ...previous, [id]: {
                    status, message: data.ocr_user_message || '',
                } }));
                if (status === 'processing') timers.push(setTimeout(() => poll(id), 3000));
                else await refresh();
            } catch (failure) {
                if (!cancelled) setTracking(previous => ({ ...previous, [id]: {
                    status: 'poll_error', message: versionComparisonError(failure),
                } }));
            }
        };
        pollingIds.split(',').forEach(id => timers.push(setTimeout(() => poll(id), 3000)));
        return () => { cancelled = true; timers.forEach(clearTimeout); };
    }, [pollingIds, refresh]);

    const importFile = async file => {
        if (!file || importing) return;
        if (!documentId || !viewedVersionId) {
            setError('Le document principal n’est pas encore identifié. Rechargez l’analyse avant d’importer une version.');
            return;
        }
        setImporting(true);
        setError('');
        setNotice('');
        try {
            const result = await importComparisonVersion(documentId, file);
            if (!mounted.current || scopeRef.current !== scope) return;
            const duplicate = rows.some(row => row.document_version_id === result.document_version_id);
            setNotice(duplicate ? 'Version existante réutilisée.' : 'Version reçue. Aucune comparaison n’a été lancée.');
            setRows(previous => previous.some(row => row.document_version_id === result.document_version_id)
                ? previous : [...previous, {
                    ...result, title: file.name, eligible_for_comparison: false,
                    ineligibility_reason: 'extraction_processing',
                }]);
            setTracking(previous => ({ ...previous, [result.document_version_id]: {
                status: result.extraction_status,
            } }));
            await refresh();
        } catch (failure) {
            if (mounted.current && scopeRef.current === scope) setError(versionComparisonError(failure));
        } finally {
            if (mounted.current && scopeRef.current === scope) setImporting(false);
        }
    };

    const choose = (id, opposite, setter) => {
        if (!id || (id !== opposite && rows.some(row => row.document_version_id === id && row.eligible_for_comparison))) setter(id);
    };
    return {
        mode, setMode, rows, referenceId, examinedId, loading, importing, error, notice, tracking,
        refresh, importFile, viewedVersionId,
        selectReference: id => choose(id, examinedId, setReferenceId),
        retry: id => setTracking(previous => ({ ...previous, [id]: { status: 'processing' } })),
    };
}
